if ( typeof ProductPage !== 'function' ) {

	class ProductPage extends HTMLElement {

		constructor(){

			super();

			this.productGallery = this.querySelector('[data-js-product-gallery]');
			this.productSlider = this.querySelector('css-slider');

			this.pickupAvailabilityExtended = this.querySelector('pickup-availability-extended');

			// Gallery thumbnails

			if ( this.productSlider ) {

				const productGaleryThumbnails = this.querySelector('.product-gallery__thumbnails-holder');

				if ( this.querySelector('.product-gallery__thumbnails .thumbnail') ) {

					// The rail runs horizontally under the image or vertically
					// beside it, depending on the `thumbnail_position` setting and
					// the viewport. Everything below that touches scroll geometry
					// has to know which, and CSS is the only thing that actually
					// decides — so ask the computed flex-direction rather than
					// re-deriving the breakpoint here and letting the two drift.
					//
					// Cached, not read per event: this is a layout-flushing read
					// and updateThumbnailScrollState runs on every scroll frame.
					let railIsVertical = false;
					const measureRailAxis = ()=>{
						railIsVertical = getComputedStyle(productGaleryThumbnails).flexDirection === 'column';
					};
					measureRailAxis();

					// section-main-product.css drops the rail to scroll-behavior:auto
					// under reduced motion, but a scrollTo() asking for 'smooth'
					// overrides that per spec — so the preference has to be honoured
					// here too or the CSS half of it is decorative.
					const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

					// Bring a tile into view along whichever axis the rail runs.
					// Vertically the tile is centred rather than pinned to the top:
					// only ~4 of 19 fit beside the image, so centring keeps the
					// neighbours either side of the current slide visible and the
					// rail readable as a sequence.
					const revealThumbnail = (elm)=>{
						if ( !elm ) return;
						const behavior = reduceMotion.matches ? 'auto' : 'smooth';
						if ( railIsVertical ) {
							// Measured off bounding rects, not offsetTop: the holder is
							// position:absolute in the vertical rail and static in the
							// horizontal one, so the offsetParent differs between them.
							const rail = productGaleryThumbnails.getBoundingClientRect();
							const tile = elm.getBoundingClientRect();
							productGaleryThumbnails.scrollTo({
								top: productGaleryThumbnails.scrollTop + (tile.top - rail.top) - (rail.height - tile.height) / 2,
								behavior
							});
						} else {
							// `behaviour` (en-GB) is not a valid ScrollToOptions key, so
							// this silently jumped instead of animating.
							productGaleryThumbnails.scrollTo({
								left: elm.offsetLeft - 50,
								behavior
							});
						}
					};
					// thumbnailNavigationHelper() is a class method and cannot see
					// these constructor-scoped consts. Handing it the reveal keeps
					// one code path for every way the slide can change — arrows,
					// dots, swipe, variant selection and thumbnail clicks alike.
					this.revealThumbnail = revealThumbnail;

					this.querySelectorAll('.product-gallery__thumbnails .thumbnail').forEach((elm, i)=>{
						if ( i == 0 )  {
							elm.classList.add('active');
						}
						elm.addEventListener('click',e=>{
							if ( this.productSlider.sliderEnabled ) {
								this.productSlider.changeSlide(e.currentTarget.dataset.index);
							} else {
								window.scrollTo({
									top: this.productGallery.querySelector(`.product-gallery-item[data-index="${e.currentTarget.dataset.index}"]`).getBoundingClientRect().top,
									behavior: 'smooth'
								});
								this.thumbnailNavigationHelper(e.currentTarget.dataset.index);
							}
							// Clicking a half-visible tile should bring it fully into
							// view even when the slide index does not change, so this
							// stays alongside the reveal in thumbnailNavigationHelper.
							// Scrolling twice to one target is a no-op the second time.
							revealThumbnail(elm);
						});
					})

					// Keep the rail's edge fade honest. The scrollbar is suppressed in
					// CSS, so the fade is the only thing telling you there are more
					// than nineteen thumbnails — it must not imply more to scroll to
					// once you are at an end.
					const updateThumbnailScrollState = ()=>{
						// Read the axis the rail actually scrolls on. A vertical rail
						// measured with scrollWidth/scrollLeft reports max 0 and pos 0,
						// which sets data-at-start AND data-at-end — and that pair
						// resolves the mask to `none`, deleting the fade on the one
						// layout where nineteen tiles make it essential.
						const max = railIsVertical
							? productGaleryThumbnails.scrollHeight - productGaleryThumbnails.clientHeight
							: productGaleryThumbnails.scrollWidth - productGaleryThumbnails.clientWidth;
						const pos = Math.abs(railIsVertical
							? productGaleryThumbnails.scrollTop
							: productGaleryThumbnails.scrollLeft);
						productGaleryThumbnails.toggleAttribute('data-at-start', pos <= 1);
						productGaleryThumbnails.toggleAttribute('data-at-end', pos >= max - 1);
					};
					productGaleryThumbnails.addEventListener('scroll', updateThumbnailScrollState, { passive: true });
					// Resize can cross the 1024px line and flip the rail's axis, so
					// re-measure before re-reading the scroll position.
					window.addEventListener('resize', ()=>{
						measureRailAxis();
						updateThumbnailScrollState();
					});
					updateThumbnailScrollState();

					this.productSlider.addEventListener('change', e=>{
						this.thumbnailNavigationHelper(e.target.index);
					});

				}

				// ── Navigation frame: pin the overlay to the VISIBLE slide ──
				// <css-slider> runs with autoHeight:false, so its box is as tall
				// as the TALLEST slide — 487.5px on Skin Fluff at 390px, set by
				// the two 4:5 infographics sitting among 8:9 photographs.
				// .css-slider-navigation-container spans that box, so on every
				// shorter slide the dots pinned to its bottom edge land in the
				// ~49px of dead page BELOW the photograph rather than on it, and
				// the chevrons centre on that dead space too. Measured on slide
				// 0: image bottom 519.8, dots bottom 548.5 — 28.8px adrift. The
				// bug hides on whichever slide happens to be the tallest, which
				// is why it reads as "only on some images".
				//
				// autoHeight:true would fix it by collapsing the box per slide,
				// but it reflows everything under the gallery on every swipe —
				// the add-to-cart button moves under the reader's thumb
				// mid-gesture. So the box keeps its constant height and only the
				// OVERLAY is resized, here, to the slide actually on screen.
				//
				// The stylesheet reads this as
				// `height: var(--pdp-active-slide-h, 100%)`, so the fallback is
				// exactly today's behaviour: failing to measure degrades to the
				// old layout, never to a collapsed container.
				const syncNavFrame = (index=0)=>{

					// indexedItems is the slider's own index-aligned list, so
					// slides[index] is the slide it scrolled to by construction.
					// Querying [data-index="N"] instead would trust an attribute
					// written by two different places — product-media.liquid and
					// component-slider.js:479.
					const slides = this.productSlider.indexedItems;
					if ( !slides || !slides.length ) return;

					// A 'change' can outrun a reset that shortened the list.
					const active = slides[Math.min(index, slides.length - 1)];
					if ( !active ) return;

					const height = active.getBoundingClientRect().height;

					// Zero means the gallery has not laid out yet (or the tab is
					// hidden). Leaving the property unset keeps the 100%
					// fallback rather than collapsing the overlay to nothing.
					if ( !height ) return;

					this.productGallery.style.setProperty('--pdp-active-slide-h', `${height}px`);

					// ── The same slack, one layer further out: the rail seam ──
					// The overlay above is position:absolute, so resizing it moves
					// nothing else on the page. The thumbnail rail is not — below
					// 1024px it sits in flow UNDER the slider box, so that box's
					// tallest-slide height pushes it that far below the photograph
					// and the comp's 1px hairline seam
					// (docs/superpowers/specs/2026-08-10-mobile-pdp-gallery-design.md,
					// "The 1px seam is the system") opens into page cream. Measured
					// on skin-fluff: 49.75px at 390px, 44.95px at 768px, 61.52px at
					// 1023px. Only the tallest slide reads correctly, which is why
					// this gets reported as "only on some images".
					//
					// Published here, applied in section-main-product.css: JS
					// measures, CSS decides where a rail below the image exists.
					// Same division of labour as --pdp-active-slide-h above, and
					// the same failure mode — unset resolves to 0, which is exactly
					// today's geometry, so no-JS degrades instead of jumping.
					//
					// This is NOT the "re-covering" the natural-frame note at the
					// foot of the stylesheet warns against. No slide is cropped,
					// resized or equalised; the rail is moved up to meet the slide
					// already on screen.
					//
					// The gallery therefore DOES change height between slides of
					// different shapes, which the autoHeight:false note above
					// rules out for the overlay. That note still holds for what it
					// covers — autoHeight:true collapses the box mid-drag, whereas
					// this fires on 'change', after the slide has settled — and on
					// mobile the add-to-cart button it protects is ~1000px below
					// the fold, so a gallery swipe cannot move it under the thumb.
					// Padding the slack back onto the gallery to pin the height was
					// tried and reverted: it only moved the dead cream below the
					// rail, where it read as a 90px gap to the product eyebrow.
					const slack = this.productSlider.getBoundingClientRect().height - height;
					this.productGallery.style.setProperty('--pdp-slide-slack', `${Math.max(0, slack)}px`);
				};

				// ── Five-dot pagination window ──
				// The Figma comp draws five dots; this product carries up to
				// nineteen media. Rather than render nineteen, the track slides
				// behind the 66px port that section-main-product.css opens
				// below 768px, keeping the active dot in the middle three.
				//
				// Outside the thumbnails guard above on purpose: the dots exist
				// whether or not the rail does.
				const syncDotWindow = (index=0)=>{

					// Looked up per call, never cached in the constructor:
					// .pdp-dots-window does not exist yet at construction time.
					// css-slider injects it from the thumbnailsDOM option during
					// its own init, so a constructor-time reference is null and
					// would disable this for the life of the page.
					const dotWindow = this.querySelector('.pdp-dots-window');
					if ( !dotWindow ) return;

					const track = dotWindow.querySelector('.css-slider-dot-navigation');
					if ( !track ) return;

					// From 768px up the port is display:contents, which gives it
					// no box at all — clientWidth reads 0, so a scrollWidth
					// comparison would wrongly conclude it is clipping. Test the
					// display directly instead.
					if ( getComputedStyle(dotWindow).display === 'contents' ) {
						track.style.transform = '';
						return;
					}

					const dots = track.querySelectorAll('.css-slider-dot');
					if ( dots.length < 2 ) return;

					// Nothing to slide when the whole strip already fits, which
					// covers products carrying five media or fewer.
					if ( track.scrollWidth <= dotWindow.clientWidth ) {
						track.style.transform = '';
						return;
					}

					// Pitch is read off the DOM rather than hardcoded at 14, so
					// dot size and gap stay owned by the stylesheet.
					const pitch = dots[1].offsetLeft - dots[0].offsetLeft;
					const start = Math.min(Math.max(index - 2, 0), Math.max(dots.length - 5, 0));
					track.style.transform = `translateX(${-start * pitch}px)`;
				};

				// css-slider may upgrade before or after <product-page>, so
				// 'ready' alone is not a reliable first sync — it can have
				// fired already. rAF and load cover both orderings; each call
				// is idempotent.
				// Both syncs share one entry point: the dot window and the
				// overlay frame are driven by the same index and must never
				// disagree about which slide is current.
				const syncGalleryNav = (index=0)=>{
					syncNavFrame(index);
					syncDotWindow(index);
				};

				this.productSlider.addEventListener('ready', ()=> syncGalleryNav(this.productSlider.index || 0));
				this.productSlider.addEventListener('change', e=> syncGalleryNav(e.target.index));
				window.addEventListener('resize', ()=> syncGalleryNav(this.productSlider.index || 0));
				window.addEventListener('load', ()=> syncGalleryNav(this.productSlider.index || 0));
				requestAnimationFrame(()=> syncGalleryNav(this.productSlider.index || 0));

				this.productSlider.addEventListener('change', e=>{
					if ( this.productGallery.querySelector(`.product-gallery-item[data-index="${e.target.index}"]`).dataset.productMediaType == 'model' ) {
						if ( this.xrButton ) {
							this.xrButton.setAttribute('data-shopify-model3d-id', this.productGallery.querySelector(`.product-gallery-item[data-index="${e.target.index}"]`).dataset.mediaId);
						}
						setTimeout(()=>{
							this.productSlider.querySelector('.css-slider-holder').classList.add('css-slider--disable-dragging');
						}, 150);
					}
				});

				// Parallax

				this.productSlider.addEventListener('ready', (e)=>{
					if ( this.firstProductGalleryIndex ) {
						this.productSlider.changeSlide(this.firstProductGalleryIndex, "auto");
					}
				});

			}

			// Product variant event listener for theme specific logic

			this.productVariants = this.querySelector('product-variants[data-main-product-variants]');
			if ( this.productVariants ) {

				this.productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChangeHandler.bind(this));
				this.onVariantChangeHandler({target:this.productVariants});

				// refresh color
				this.querySelectorAll('.product-variant__item--radio input').forEach(input=>{
					input.addEventListener('click', () => {
						const inputParent = input.closest('.product-variant__item--radio');
						if ( inputParent.classList.contains('product-variant__item--color' ) ) {
							inputParent.closest('.product-variant').querySelector('.product-variant__item-text-label').textContent = input.value;
						}
					})
				});

			}

			// show cart drawer when element is added to cart

			if ( ! document.body.classList.contains('template-cart') && KROWN.settings.cart_action == 'overlay' ) {
				
				let addToCartEnter = false;
				if ( this.querySelector('[data-js-product-add-to-cart]') ) {
					this.querySelector('[data-js-product-add-to-cart]').addEventListener('keyup', e=>{
						if ( e.keyCode == window.KEYCODES.RETURN ) {
							addToCartEnter = true;
						}
					})
				}	

				if ( this.querySelector('[data-js-product-form]') ) {
					this.querySelector('[data-js-product-form]').addEventListener('add-to-cart', ()=>{
						document.getElementById('site-cart-sidebar').show();
							if ( document.getElementById('cart-recommendations-sidebar') ) {
							document.getElementById('cart-recommendations-sidebar').generateRecommendations();
						}
						if ( addToCartEnter ) {
							setTimeout(()=>{
								document.querySelector('#site-cart-sidebar [data-js-close]').focus();
							}, 200);
						}
					});
				}

			}

			// Check for models

			const models = this.querySelectorAll('product-model');
			if ( models.length > 0 ) {
				window.ProductModel.loadShopifyXR();
				this.xrButton = this.querySelector('.product-gallery__view-in-space');
			}

			// hide buy now button if stock disabled

			const addToCartButton = this.querySelector('[data-js-product-add-to-cart][data-main-product-add-to-cart]');
			if ( addToCartButton ) {
				if ( addToCartButton.classList.contains('disabled') ) {
					this.querySelector('product-form').classList.add('disable-buy-button');
				} else {
					this.querySelector('product-form').classList.remove('disable-buy-button');
				}
				const buyObserver = new MutationObserver(()=>{
					if ( addToCartButton.classList.contains('disabled') ) {
						this.querySelector('product-form').classList.add('disable-buy-button');
					} else {
						this.querySelector('product-form').classList.remove('disable-buy-button');
					}
				});
				buyObserver.observe(addToCartButton,{ attributes: true, childList: false, subtree: false });
			}

		}

		thumbnailNavigationHelper(index=0){
			this.querySelectorAll('.product-gallery__thumbnails .thumbnail').forEach((elm, i)=>{
				// The gallery index is navigation, not decoration, so the current
				// slide has to be exposed to assistive tech and not only painted.
				if ( i == index ) {
					elm.classList.add('active');
					elm.setAttribute('aria-current', 'true');
				} else {
					elm.classList.remove('active');
					elm.removeAttribute('aria-current');
				}
			});

			// Keep the rail pointed at the current slide. Horizontally this was
			// merely nice; in the vertical rail only ~4 of 19 tiles fit beside the
			// image, so without it pressing the arrow moves the hero while the
			// active tile sits somewhere off-screen. Optional call: products with
			// a single medium have no rail and never get the helper assigned.
			this.revealThumbnail?.(this.querySelector('.product-gallery__thumbnails .thumbnail.active'));
		}

		onVariantChangeHandler(e){

			let variant = e.target.currentVariant;
			if ( variant ) {
				
				// image handling

				if ( variant.featured_media != null ) {
					let variantImg = this.productGallery.querySelector('.product-gallery-item[data-media-id="' + variant.featured_media.id + '"]');
					if ( variantImg ) {
						if ( this.productGallery.classList.contains('product-gallery--slider') || ( this.productGallery.classList.contains('product-gallery--scroll') && window.innerWidth < 1024 ) ) {
							if ( this.productGallery.querySelector('css-slider') && this.productGallery.querySelector('css-slider').sliderEnabled ) {
								this.productGallery.querySelector('css-slider').changeSlide(variantImg.dataset.index);
							} else {
								this.firstProductGalleryIndex = variantImg.dataset.index;
							}
						} else {
							if ( variantImg.getBoundingClientRect().top > window.innerHeight ) {		
								variantImg.scrollIntoView({behavior: 'smooth', block: 'center'});
							} else if ( variantImg.getBoundingClientRect().top < 0 ) {
								variantImg.scrollIntoView({behavior: 'smooth', block: 'center'});
							}
						}
					}
				}
				
				this.querySelectorAll('.product-variant__item--color [data-selected]').forEach(elm=>{
					elm.closest('.product-variant').querySelector('.product-variant__item-text-label').textContent = elm.value;
				});

			}

		}
		
	}

  if ( typeof customElements.get('product-page') == 'undefined' ) {
		customElements.define('product-page', ProductPage);
	}

}